import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { useDrugs, useCreateDrug, useUpdateDrug, useDeleteDrug } from '../../hooks/useDrugs';
import { PageHeader } from '../../components/common/PageHeader';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { DrugForm } from '../../components/drugs/DrugForm';
import { Table } from '../../components/common/Table';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Drug, CreateDrugDto, UpdateDrugDto } from '../../types/drug.types';
import toast from 'react-hot-toast';

export const DrugsPage: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDrug, setEditingDrug] = useState<Drug | null>(null);
  const [deleteDrugId, setDeleteDrugId] = useState<number | null>(null);

  const { data: drugs, isLoading, error } = useDrugs();
  const createMutation = useCreateDrug();
  const updateMutation = useUpdateDrug();
  const deleteMutation = useDeleteDrug();

  const handleCreate = async (data: CreateDrugDto) => {
    try {
      await createMutation.mutateAsync(data);
      toast.success(t('drugs.drugCreated', 'Drug created successfully'));
      setIsModalOpen(false);
    } catch (error) {
      toast.error(t('drugs.drugCreatedError', 'Failed to create drug'));
    }
  };

  const handleUpdate = async (data: UpdateDrugDto) => {
    if (!editingDrug) return;
    
    try {
      await updateMutation.mutateAsync({ id: editingDrug.id, data });
      toast.success(t('drugs.drugUpdated', 'Drug updated successfully'));
      setIsModalOpen(false);
      setEditingDrug(null);
    } catch (error) {
      toast.error(t('drugs.drugUpdatedError', 'Failed to update drug'));
    }
  };

  const handleDelete = async () => {
    if (!deleteDrugId) return;
    
    try {
      await deleteMutation.mutateAsync(deleteDrugId);
      toast.success(t('drugs.drugDeleted', 'Drug deleted successfully'));
      setDeleteDrugId(null);
    } catch (error) {
      toast.error(t('drugs.drugDeletedError', 'Failed to delete drug'));
    }
  };

  const handleEdit = (drug: Drug) => {
    setEditingDrug(drug);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDrug(null);
  };

  return (
    <div className="w-100" style={{ maxWidth: '100%', overflowX: 'hidden' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        title={t('drugs.title', 'Drugs Management')}
        actions={
          <Button
            variant="primary"
            onClick={() => setIsModalOpen(true)}
          >
            {isRTL ? (
              <>
                <Plus className="ms-2" size={16} />
                {t('drugs.addDrug', 'Add Drug')}
              </>
            ) : (
              <>
                <Plus className="me-2" size={16} />
                {t('drugs.addDrug', 'Add Drug')}
              </>
            )}
          </Button>
        }
      />

      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {error ? (
                <div className="p-4">
                  <ErrorMessage message={error.message || t('errors.required')} />
                </div>
              ) : (
                <Table
                  data={Array.isArray(drugs) ? drugs : []}
                  columns={[
                    {
                      key: 'name',
                      header: t('drugs.name', 'Name'),
                      render: (drug) => (
                        <span className="fw-semibold text-dark">{drug.name}</span>
                      ),
                      sortable: true,
                      width: '30%',
                    },
                    {
                      key: 'description',
                      header: t('drugs.description', 'Description'),
                      render: (drug) => (
                        <span className="text-muted">{drug.description || '-'}</span>
                      ),
                      sortable: true,
                      width: '40%',
                    },
                    {
                      key: 'unit',
                      header: t('drugs.unit', 'Unit'),
                      render: (drug) => (
                        <span className="badge bg-light text-dark">{drug.unit || '-'}</span>
                      ),
                      sortable: true,
                      width: '15%',
                    },
                    {
                      key: 'actions',
                      header: t('common.actions', 'Actions'),
                      render: (drug) => (
                        <div className={`d-flex gap-2 ${isRTL ? 'justify-content-start' : 'justify-content-end'}`}>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              handleEdit(drug);
                            }}
                            className="p-2"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={(e) => {
                              e?.stopPropagation();
                              setDeleteDrugId(drug.id);
                            }}
                            className="p-2"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ),
                      width: '15%',
                    },
                  ]}
                  keyExtractor={(drug) => drug.id}
                  emptyMessage={t('drugs.noDrugs', 'No drugs found')}
                  pageSize={15}
                  isLoading={isLoading}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingDrug ? t('drugs.editDrug', 'Edit Drug') : t('drugs.addDrug', 'Add Drug')}
      >
        <DrugForm
          drug={editingDrug || undefined}
          onSubmit={editingDrug ? handleUpdate : handleCreate}
          isLoading={createMutation.isPending || updateMutation.isPending}
          onCancel={handleCloseModal}
        />
      </Modal>

      <ConfirmDialog
        isOpen={deleteDrugId !== null}
        onClose={() => setDeleteDrugId(null)}
        onConfirm={handleDelete}
        title={t('common.delete')}
        message={t('drugs.deleteConfirm', 'Are you sure you want to delete this drug?')}
        confirmText={t('common.delete')}
        variant="danger"
      />
    </div>
  );
};

